// @ts-expect-error
import { API_ID } from '../../../modules/API';
import { setRoom } from '../base/conference/actions';
import { applyVideoMailRoomConfig } from '../voicemail/recordingRoom';
import { configWillLoad, setConfig } from '../base/config/actions';
import { getRoomNameFromPath } from '../base/config/getRoomName';
import { setLocationURL } from '../base/connection/actions.web';
import { loadConfig } from '../base/lib-jitsi-meet/functions.web';
import { updateSettings } from '../base/settings/actions';
import { inIframe } from '../base/util/iframeUtils';
import { parseURIString } from '../base/util/uri';
import { isVpaasMeeting } from '../jaas/functions';
import { clearNotifications, showErrorNotification, showNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { setPrejoinPageVisibility } from '../prejoin/actions.web';
import { isWelcomePageEnabled } from '../welcome/functions';
import { queueService } from '../interpreter-queue/InterpreterQueueService';
import { findGroupRejoin, groupRejoinFailureMessage, requestGroupRejoin } from '../interpreter-queue/groupRejoin';
import { runCallOpenElsewhereChoice } from '../interpreter-queue/accountDeviceNotices';
import { dismissAccountDeviceNotice, presentAccountDeviceNotice } from '../interpreter-queue/accountDevicePresenter.web';
import { RoomAuthorizationError, addJitsiRoomToken, getRoomJoinFailureMessage, withMoveCall, withoutMoveCall } from '../vrs-auth/jitsiRoomToken';
import { getPersistentJson, setPersistentItem } from '../vrs-auth/storage';
import { maybeRedirectToTokenAuthUrl, redirectToStaticPage, redirectWithStoredParams, reloadWithStoredParams } from './actions.any';
import { getDefaultURL, getName } from './functions.web';
import logger from './logger';
import { replaceRoomTokenInCurrentURL } from './replaceRoomTokenInCurrentURL.web';
export * from './actions.any';
function getVrsAuthenticatedReturnPath() {
    if (typeof window === 'undefined') {
        return;
    }
    const token = window.localStorage.getItem('vrs_auth_token') || window.sessionStorage.getItem('vrs_auth_token');
    const userInfo = window.localStorage.getItem('vrs_user_info') || window.sessionStorage.getItem('vrs_user_info');
    if (!token || !userInfo) {
        return;
    }
    try {
        const { role } = JSON.parse(userInfo);
        if (role === 'client') {
            return '/';
        }
        if (role === 'interpreter') {
            return '/interpreter-profile.html';
        }
        if (role === 'captioner') {
            return '/captioner-profile.html';
        }
    }
    catch {
        return;
    }
}
let callOpenElsewhereChoice;
/**
 * The room's call is open on another device of this client account (409
 * CALL_ACTIVE_ON_OTHER_DEVICE). This used to end in a toast on a dead page.
 * Instead: say which device and for how long, and offer "Move call here" or
 * "Wait" — waiting re-tries every 5 s, joins by itself once the other device
 * leaves a call that continues, and says so if the call ended.
 *
 * @param {Function} dispatch - Redux dispatch.
 * @param {URL} locationURL - The room URL that was refused.
 * @param {string} room - The room name.
 * @param {AccountCallHold} [hold] - Which device holds the call, from the 409.
 * @returns {void}
 */
function offerCallOpenElsewhere(dispatch, locationURL, room, hold) {
    const roomURL = withoutMoveCall(locationURL);
    let authorizedURL;
    callOpenElsewhereChoice?.stop();
    callOpenElsewhereChoice = runCallOpenElsewhereChoice({
        hold: hold || {},
        present: presentAccountDeviceNotice,
        dismiss: dismissAccountDeviceNotice,
        attempt: async () => {
            try {
                authorizedURL = await addJitsiRoomToken(roomURL, room);
                return { outcome: 'joined' };
            }
            catch (error) {
                if (error instanceof RoomAuthorizationError) {
                    if (error.code === 'CALL_ACTIVE_ON_OTHER_DEVICE') {
                        return { outcome: 'held', hold: error.hold };
                    }
                    if (error.status === 403) {
                        return { outcome: 'ended' };
                    }
                }
                return { outcome: 'failed', message: getRoomJoinFailureMessage(error) };
            }
        },
        // The other device left and the call goes on: the user already asked
        // to join, so join, with the token just minted.
        onJoin: () => {
            void dispatch(appNavigate((authorizedURL || roomURL).toString()));
        },
        onMove: () => {
            void Promise.resolve(dispatch(appNavigate(withMoveCall(roomURL).toString()))).then((joined) => {
                // This device now carries the call: record it like a matched
                // call so leaving it hangs up and the room reports it joined.
                const stored = getPersistentJson('vrs_active_call');
                if (joined === true && hold?.callId && stored?.roomName !== room) {
                    setPersistentItem('vrs_active_call', JSON.stringify({
                        callId: hold.callId,
                        roomName: room,
                        role: 'client',
                        startedAt: Date.now()
                    }));
                }
            });
        },
        onHome: () => {
            window.location.assign(getVrsAuthenticatedReturnPath() || '/');
        }
    });
}
/**
 * Triggers an in-app navigation to a specific route. Allows navigation to be
 * abstracted between the mobile/React Native and Web/React applications.
 *
 * @param {string|undefined} uri - The URI to which to navigate. It may be a
 * full URL with an HTTP(S) scheme, a full or partial URI with the app-specific
 * scheme, or a mere room name.
 * @returns {Function}
 */
export function appNavigate(uri, _options) {
    return async (dispatch, getState) => {
        let location = parseURIString(uri);
        // If the specified location (URI) does not identify a host, use the app's
        // default.
        if (!location?.host) {
            const defaultLocation = parseURIString(getDefaultURL(getState));
            if (location) {
                location.host = defaultLocation.host;
                // FIXME Turn location's host, hostname, and port properties into
                // setters in order to reduce the risks of inconsistent state.
                location.hostname = defaultLocation.hostname;
                location.pathname
                    = defaultLocation.pathname + location.pathname.substr(1);
                location.port = defaultLocation.port;
                location.protocol = defaultLocation.protocol;
            }
            else {
                location = defaultLocation;
            }
        }
        location.protocol || (location.protocol = 'https:');
        const room = getRoomNameFromPath(location.pathname);
        // parseURIString treats every final path segment as a room. Keep public
        // app routes out of conference authorization and prejoin entirely.
        location.room = room;
        let locationURL = new URL(location.toString());
        // Reloading a group call's room left the call (the pill's pagehide
        // call_end). Ask to be seated again before joining: the server
        // refuses the room to a member who has left.
        const rejoin = room ? findGroupRejoin(room) : null;
        if (rejoin) {
            const outcome = await requestGroupRejoin(queueService, rejoin);
            if (!outcome.ok) {
                logger.warn('Group call rejoin refused', outcome.code);
                dispatch(showErrorNotification({
                    description: groupRejoinFailureMessage(outcome.code),
                    title: 'Could not rejoin the call'
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
                return false;
            }
        }
        if (room) {
            try {
                locationURL = await addJitsiRoomToken(locationURL, room);
            }
            catch (error) {
                logger.error('Failed to authorize secure room navigation.', error);
                if (error instanceof RoomAuthorizationError && error.code === 'CALL_ACTIVE_ON_OTHER_DEVICE') {
                    offerCallOpenElsewhere(dispatch, locationURL, room, error.hold);
                    return false;
                }
                dispatch(showErrorNotification({
                    description: getRoomJoinFailureMessage(error),
                    title: 'Secure room access failed'
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
                return false;
            }
        }
        // There are notifications now that gets displayed after we technically left
        // the conference, but we're still on the conference screen.
        dispatch(clearNotifications());
        dispatch(configWillLoad(locationURL, room ?? ''));
        const config = await loadConfig();
        if (_options?.startWithAudioMuted !== undefined) {
            config.startWithAudioMuted = _options.startWithAudioMuted;
        }
        if (_options?.startWithVideoMuted !== undefined) {
            config.startWithVideoMuted = _options.startWithVideoMuted;
            if (_options.startWithVideoMuted === false) {
                config.startAudioOnly = false;
            }
        }
        if (_options?.startWithAudioMuted !== undefined || _options?.startWithVideoMuted !== undefined) {
            dispatch(updateSettings({
                ...(_options.startWithAudioMuted !== undefined
                    ? { startWithAudioMuted: _options.startWithAudioMuted }
                    : {}),
                ...(_options.startWithVideoMuted !== undefined
                    ? {
                        ...(_options.startWithVideoMuted === false ? { startAudioOnly: false } : {}),
                        startWithVideoMuted: _options.startWithVideoMuted
                    }
                    : {})
            }));
        }
        if (_options?.hidePrejoin) {
            dispatch(setPrejoinPageVisibility(false));
        }
        if (room) {
            replaceRoomTokenInCurrentURL(locationURL);
        }
        dispatch(setLocationURL(locationURL));
        // A VideoMail room hides the recorder, not the observer.
        dispatch(setConfig(applyVideoMailRoomConfig(config, room)));
        dispatch(setRoom(room));
        return true;
    };
}
/**
 * Check if the welcome page is enabled and redirects to it.
 * If requested show a thank you dialog before that.
 * If we have a close page enabled, redirect to it without
 * showing any other dialog.
 *
 * @param {Object} options - Used to decide which particular close page to show
 * or if close page is disabled, whether we should show the thankyou dialog.
 * @param {boolean} options.showThankYou - Whether we should
 * show thank you dialog.
 * @param {boolean} options.feedbackSubmitted - Whether feedback was submitted.
 * @returns {Function}
 */
export function maybeRedirectToWelcomePage(options = {}) {
    return (dispatch, getState) => {
        const vrsReturnPath = getVrsAuthenticatedReturnPath();
        if (vrsReturnPath) {
            dispatch(redirectToStaticPage(vrsReturnPath));
            return;
        }
        const { enableClosePage } = getState()['features/base/config'];
        // if close page is enabled redirect to it, without further action
        if (enableClosePage) {
            if (isVpaasMeeting(getState())) {
                const isOpenedInIframe = inIframe();
                if (isOpenedInIframe) {
                    // @ts-ignore
                    window.location = 'about:blank';
                }
                else {
                    dispatch(redirectToStaticPage('/'));
                }
                return;
            }
            const { jwt } = getState()['features/base/jwt'];
            let hashParam;
            // save whether current user is guest or not, and pass auth token,
            // before navigating to close page
            window.sessionStorage.setItem('guest', (!jwt).toString());
            window.sessionStorage.setItem('jwt', jwt ?? '');
            let path = 'close.html';
            if (interfaceConfig.SHOW_PROMOTIONAL_CLOSE_PAGE) {
                if (Number(API_ID) === API_ID) {
                    hashParam = `#jitsi_meet_external_api_id=${API_ID}`;
                }
                path = 'close3.html';
            }
            else if (!options.feedbackSubmitted) {
                path = 'close2.html';
            }
            dispatch(redirectToStaticPage(`static/${path}`, hashParam));
            return;
        }
        // else: show thankYou dialog only if there is no feedback
        if (options.showThankYou) {
            dispatch(showNotification({
                titleArguments: { appName: getName() },
                titleKey: 'dialog.thankYou'
            }, NOTIFICATION_TIMEOUT_TYPE.STICKY));
        }
        // if Welcome page is enabled redirect to welcome page after 3 sec, if
        // there is a thank you message to be shown, 0.5s otherwise.
        if (isWelcomePageEnabled(getState())) {
            setTimeout(() => {
                dispatch(redirectWithStoredParams('/'));
            }, options.showThankYou ? 3000 : 500);
        }
    };
}
/**
 * Reloads the page.
 *
 * @protected
 * @returns {Function}
 */
export function reloadNow() {
    return (dispatch, getState) => {
        const state = getState();
        const { locationURL } = state['features/base/connection'];
        const reloadAction = () => {
            logger.info(`Reloading the conference using URL: ${locationURL}`);
            dispatch(reloadWithStoredParams());
        };
        if (maybeRedirectToTokenAuthUrl(dispatch, getState, reloadAction)) {
            return;
        }
        reloadAction();
    };
}
